/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Lista de pagos (desk) — unificada a TicketListRow (Square).
 * La tabla Excel de 6 columnas fue eliminada en la migración Square.
 */
import React from 'react';
import { TicketListRow } from '../../shared/ticket/TicketListRow';
import type { ExpenseSettlementListRow } from '../domain/expenseTypes';
import {
  expenseSettlementMethodLabel,
  expenseSettlementStatusLabel,
  formatExpenseDateShort,
  formatExpenseMoney,
} from '../domain/expenseLabels';
import { formatLocalDateTime } from '@shared/vueltoDateTime';

type Props = {
  rows: ExpenseSettlementListRow[];
  onOpenPayment: (row: ExpenseSettlementListRow) => void;
};

export function ExpensePaymentsTable({ rows, onOpenPayment }: Props) {
  if (rows.length === 0) return null;

  return (
    <>
      {rows.map((row, index) => {
        const isVoided = row.settlementStatus === 'voided';
        // Proto: una sola línea — fecha/hora · categoría · proveedor · nota · anulado.
        // "gasto DD/MM" solo cuando la factura es de otro día.
        const paidDay = (row.paidAt ?? '').slice(0, 10);
        const metaParts = [
          formatLocalDateTime(row.paidAt),
          row.categoryName,
          row.supplierName,
          row.expenseDate && row.expenseDate.slice(0, 10) !== paidDay
            ? `gasto ${formatExpenseDateShort(row.expenseDate)}`
            : null,
        ].filter(Boolean);
        if (row.note) metaParts.push(row.note);
        if (isVoided && row.voidReason) metaParts.push(`anulado: ${row.voidReason}`);

        const group: 'grouped' | 'groupedLast' =
          index === rows.length - 1 ? 'groupedLast' : 'grouped';

        return (
          <TicketListRow
            key={row.settlementId}
            title={expenseSettlementMethodLabel(row.method)}
            meta={metaParts.join(' · ')}
            value={formatExpenseMoney(row.settlementAmount)}
            statusLabel={expenseSettlementStatusLabel(row.settlementStatus)}
            statusTone={isVoided ? 'muted' : 'ok'}
            inactive={isVoided}
            onPress={() => onOpenPayment(row)}
            group={group}
            accessibilityLabel={[
              isVoided ? 'Anulado' : null,
              expenseSettlementMethodLabel(row.method),
              formatExpenseMoney(row.settlementAmount),
              formatLocalDateTime(row.paidAt),
              row.supplierName,
            ]
              .filter(Boolean)
              .join(', ')}
            testID={`gastos.payment.row.${row.settlementId}`}
          />
        );
      })}
    </>
  );
}
